import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  QrCode, 
  Camera, 
  ArrowRight, 
  Search, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Clock,
  User,
  MapPin
} from 'lucide-react';
import { Card, Button, Input, Badge } from '../../components/ui/index.jsx';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../../utils/api';

const AttendanceScanner = () => {
    const navigate = useNavigate();
    const [manualCode, setManualCode] = useState('');
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('attendance', 'create');

    const [status, setStatus] = useState({ type: 'idle', message: '', student: null });
    const [stats, setStats] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState(null);
    
    const scannerRef = useRef(null);
    const lockRef = useRef(false);

    const fetchStats = async () => {
        try {
            const response = await api.get('/attendance/stats');
            setStats(response.data);
        } catch (err) {
            console.error("Failed to fetch stats", err);
        }
    };

    useEffect(() => {
        if (!canCreate) {
            navigate('/dashboard');
        }
        fetchStats();
    }, [canCreate, navigate]);

    useEffect(() => {
        const scannerId = "attendance-reader";
        const html5QrCode = new Html5Qrcode(scannerId);
        scannerRef.current = html5QrCode;

        const startScanner = async () => {
            try {
                setIsScanning(true);
                const config = { 
                    fps: 15, 
                    qrbox: { width: 280, height: 280 },
                    aspectRatio: 1.0
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        handleScanSuccess(decodedText);
                    },
                    (errorMessage) => {
                        // Silence noise
                    }
                );
            } catch (err) {
                console.error("Unable to start scanning", err);
                setError("تعذر الوصول إلى الكاميرا. يرجى التأكد من منح الأذونات اللازمة.");
                setIsScanning(false);
            }
        };

        startScanner();

        return () => {
            if (scannerRef.current && scannerRef.current.isScanning) {
                scannerRef.current.stop().catch(err => console.error(err));
            }
        };
    }, []);

    const handleScanSuccess = async (decodedText) => {
        if (lockRef.current) return;
        
        lockRef.current = true;
        setStatus({ type: 'loading', message: 'جاري تسجيل الحضور...', student: null });

        try {
            const response = await api.post('/attendance/scan', { 
                code: decodedText
            });
            const data = response.data;

            if (data.status === 'success' || data.status === 'already_registered') {
                setStatus({ 
                    type: data.status === 'success' ? 'success' : 'warning', 
                    message: data.message, 
                    student: data.student 
                });
                fetchStats();
            }
        } catch (err) {
            setStatus({ 
                type: 'error', 
                message: err.response?.data?.message || 'فشل في تسجيل الحضور' 
            });
        } finally {
            setTimeout(() => {
                setStatus({ type: 'idle', message: '', student: null });
                lockRef.current = false;
            }, 2000);
        }
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualCode.trim() && !lockRef.current) {
            handleScanSuccess(manualCode.trim());
            setManualCode('');
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl text-slate-800 font-normal">تسجيل الحضور اليومي</h2>
                    <p className="text-slate-400 text-[11px] mt-1 font-normal uppercase tracking-wider">قم بمسح الكود لتسجيل طابع زمني جديد للتلميذ</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/attendance')} className="text-slate-500">
                         عرض السجل
                    </Button>
                    <Badge variant={error ? "danger" : "primary"} className="px-3 py-1 font-normal">
                        {error ? "خطأ في الكاميرا" : "الماسح متصل"}
                    </Badge>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left: Scanner */}
                <div className="lg:col-span-7 space-y-6">
                    <Card className="relative aspect-square overflow-hidden bg-slate-950 border-none shadow-2xl flex items-center justify-center group rounded-3xl">
                        <div id="attendance-reader" className="w-full h-full [&>video]:object-cover"></div>

                        {/* Overlays */}
                        {status.type !== 'idle' && status.type !== 'loading' && (
                            <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center p-8 text-center animate-in zoom-in fade-in duration-300 ${
                                status.type === 'success' ? 'bg-emerald-500/90' : 
                                status.type === 'warning' ? 'bg-amber-500/90' : 'bg-red-500/90'
                            }`}>
                                {status.type === 'success' ? <CheckCircle2 className="w-20 h-20 text-white mb-4 animate-bounce" /> : 
                                 status.type === 'warning' ? <AlertCircle className="w-20 h-20 text-white mb-4" /> : 
                                 <XCircle className="w-20 h-20 text-white mb-4" />}
                                
                                <h3 className="text-2xl font-bold text-white mb-2">{status.message}</h3>
                                {status.student && (
                                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mt-2">
                                        <p className="text-white font-medium">{status.student.full_name}</p>
                                        <p className="text-white/70 text-[10px] uppercase font-bold mt-1 tracking-widest leading-loose">
                                            تم تسجيل التوقيت: {new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {error && (
                            <div className="absolute inset-0 z-30 bg-slate-900/95 flex flex-col items-center justify-center p-8 text-center">
                                <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                                <p className="text-white text-sm font-normal mb-6">{error}</p>
                                <Button variant="outline" onClick={() => window.location.reload()} className="border-white/20 text-white">
                                    إعادة المحاولة
                                </Button>
                            </div>
                        )}

                        {/* Target Frame */}
                        {status.type === 'idle' && !error && (
                            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center z-10">
                                <div className="w-72 h-72 border-2 border-white/10 rounded-3xl relative">
                                    <div className="absolute -top-1 -left-1 w-10 h-10 border-t-4 border-l-4 border-primary rounded-tl-xl" />
                                    <div className="absolute -top-1 -right-1 w-10 h-10 border-t-4 border-r-4 border-primary rounded-tr-xl" />
                                    <div className="absolute -bottom-1 -left-1 w-10 h-10 border-b-4 border-l-4 border-primary rounded-bl-xl" />
                                    <div className="absolute -bottom-1 -right-1 w-10 h-10 border-b-4 border-r-4 border-primary rounded-br-xl" />
                                    <div className="absolute top-0 left-0 w-full h-1 bg-primary/40 shadow-[0_0_15px_rgba(255,113,34,0.6)] animate-scan-line" />
                                </div>
                                <div className="mt-8 bg-black/40 backdrop-blur-md px-6 py-2 rounded-full border border-white/5">
                                    <p className="text-[10px] text-white uppercase tracking-widest flex items-center gap-2">
                                        جاري البحث عن كود...
                                    </p>
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card className="p-5 bg-white border-slate-100 flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 shrink-0">
                            <Search size={20} />
                        </div>
                        <form onSubmit={handleManualSubmit} className="flex-1 flex gap-2">
                            <Input 
                                placeholder="إدخال رمز التلميذ يدوياً..." 
                                value={manualCode}
                                onChange={(e) => setManualCode(e.target.value)}
                                className="h-11 bg-slate-50/50 border-slate-200 text-sm"
                                disabled={lockRef.current}
                            />
                            <Button type="submit" disabled={!manualCode.trim() || lockRef.current} className="h-11 px-6">
                                تسجيل
                            </Button>
                        </form>
                    </Card>
                </div>

                {/* Right: Last Scan & Stats */}
                <div className="lg:col-span-5 space-y-6">
                    <Card className="p-6 bg-white border-slate-100 relative overflow-hidden">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                            <Clock size={14} className="text-primary" /> آخر تلميذ مسجل
                        </h3>

                        {status.student ? (
                            <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                                <div className="flex items-center gap-4">
                                    <div className={`w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl font-black shadow-inner`}>
                                        {status.student.full_name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="text-lg font-black text-slate-800 leading-tight">{status.student.full_name}</p>
                                        <p className="text-xs text-primary font-bold uppercase tracking-tight mt-1">{status.student.code_massar}</p>
                                    </div>
                                </div>
                                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 italic text-[11px] text-slate-500">
                                    تم تسجيل دخول جديد لهذا التلميذ في تمام الساعة {new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-10 text-center opacity-30">
                                <div className="w-16 h-16 rounded-3xl border-2 border-dashed border-slate-300 flex items-center justify-center mb-4">
                                    <User size={24} className="text-slate-300" />
                                </div>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">في انتظار المسح...</p>
                            </div>
                        )}
                    </Card>

                    <Card className="p-6 bg-slate-900 border-none shadow-xl text-white overflow-hidden relative">
                        <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-primary/20 rounded-full blur-3xl"></div>
                        <h3 className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-6 flex items-center justify-between">
                            <span>إحصائيات اليوم</span>
                            <Badge variant="primary" className="bg-primary/20 text-primary border-none text-[8px]">مباشر</Badge>
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4 relative z-10">
                            <div className="p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/5">
                                <p className="text-[10px] text-white/50 font-bold mb-1 uppercase tracking-tighter">الحاضرون</p>
                                <p className="text-2xl font-black">{stats?.present || 0}</p>
                            </div>
                            <div className="p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/5">
                                <p className="text-[10px] text-white/50 font-bold mb-1 uppercase tracking-tighter">الغائبون</p>
                                <p className="text-2xl font-black">{stats?.absent || 0}</p>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-white/5">
                            <p className="text-[10px] text-white/30 leading-relaxed italic">
                                النظام يقوم الآن بتسجيل كافة حركات الدخول بمرونة تامة.
                            </p>
                        </div>
                    </Card>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes scan-line {
                    0% { top: 0%; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { top: 100%; opacity: 0; }
                }
                .animate-scan-line {
                    animation: scan-line 3s infinite linear;
                }
                #attendance-reader video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
                #attendance-reader__scan_region {
                    background: transparent !important;
                }
                #attendance-reader__dashboard {
                    display: none !important;
                }
            `}} />
        </div>
    );
};

export default AttendanceScanner;
